import { useMutation, useQuery } from '@tanstack/react-query';
import { RefreshCw, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LOADING_LABELS } from '@kexiaoman/shared';
import { adminApi } from '@/lib/api';

export function SettingsPage() {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold tracking-tight">设置</h2>
      <p className="text-sm text-muted-foreground">
        运行时可调的全局参数。改动即时生效，不需要重新部署。
      </p>
      <FxRateCard />
      <DesktopMinVersionCard />
      <DefaultSeatCreditsCard />
    </div>
  );
}

const DESKTOP_VERSION_RE = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z][0-9A-Za-z.-]*)?$/;

function DesktopMinVersionCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'desktop-min-version'],
    queryFn: adminApi.getDesktopMinVersion,
  });

  const [value, setValue] = useState('');

  useEffect(() => {
    if (data) setValue(data.value);
  }, [data]);

  const save = useMutation({
    mutationFn: () => adminApi.setDesktopMinVersion(value.trim()),
    onSuccess: () => toast.success('已保存桌面端最低版本'),
  });

  const trimmed = value.trim();
  const valid = DESKTOP_VERSION_RE.test(trimmed);
  const dirty = data ? data.value !== trimmed : false;
  const unset = data ? data.updated_at === null : false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">桌面端最低可用版本</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          用于控制强制更新。普通发新版时不要改这里；发布脚本上传 OSS feed 后，客户端会看到新版本但仍可选择稍后更新。
          <br />
          当需要拦截旧客户端时，把此值提高到目标版本，低于该版本的客户端会在启动时进入必须更新。
        </p>

        {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <>
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <Field label="最低版本">
                <Input
                  value={value}
                  placeholder="2.0.1"
                  onChange={(e) => setValue(e.target.value)}
                />
              </Field>
              <div className="flex items-end">
                <Button onClick={() => save.mutate()} disabled={!valid || !dirty || save.isPending}>
                  <Save className="h-4 w-4 mr-1" />
                  {save.isPending ? LOADING_LABELS.saving : '保存'}
                </Button>
              </div>
            </div>

            {!valid && trimmed && (
              <div className="text-xs text-destructive">版本格式应类似 2.0.1 或 2.0.2-beta.1。</div>
            )}

            <div className="text-xs text-muted-foreground space-y-0.5">
              <div>
                当前 OSS 最新版本：<code className="text-xs bg-muted px-1 rounded">{data.latestVersion}</code>
              </div>
              {unset ? (
                <div>当前使用后端回退值（尚未在数据库中设置）。</div>
              ) : (
                <div>
                  上次更新：{data.updated_at ? new Date(data.updated_at).toLocaleString('zh-CN') : 'n/a'} · by{' '}
                  <code className="text-xs bg-muted px-1 rounded">{data.updated_by ?? 'n/a'}</code>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DefaultSeatCreditsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'default-seat-credits'],
    queryFn: adminApi.getDefaultSeatCredits,
  });

  const [value, setValue] = useState('');

  useEffect(() => {
    if (data) setValue(String(data.value));
  }, [data]);

  const save = useMutation({
    mutationFn: () => adminApi.setDefaultSeatCredits(Number(value)),
    onSuccess: () => toast.success('已保存默认算力'),
  });

  const parsed = Number(value);
  const valid = Number.isFinite(parsed) && parsed > 0;
  const dirty = data ? String(data.value) !== value : false;
  const unset = data ? data.updated_at === null : false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">默认席位 / 派发 / 续费算力</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          用于新用户首次自动开通的「默认席位」，以及 Admin 手动新增席位、审批派发额度、续费弹窗的默认算力。
          <br />
          <b>不会批量改动任何已存在席位</b>；续费或派发时仍可在表单里单次覆盖。未设置数据库行时回退到环境变量
          <code className="text-xs bg-muted px-1 rounded">DEFAULT_SEAT_CREDITS</code>。
        </p>

        {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <>
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <Field label="默认算力">
                <Input
                  type="number"
                  step="1000"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </Field>
              <div className="flex items-end">
                <Button onClick={() => save.mutate()} disabled={!valid || !dirty || save.isPending}>
                  <Save className="h-4 w-4 mr-1" />
                  {save.isPending ? LOADING_LABELS.saving : '保存'}
                </Button>
              </div>
            </div>

            <div className="text-xs text-muted-foreground space-y-0.5">
              {unset ? (
                <div>当前使用环境变量默认值（尚未在数据库中设置）。</div>
              ) : (
                <div>
                  上次更新：{data.updated_at ? new Date(data.updated_at).toLocaleString('zh-CN') : 'n/a'} · by{' '}
                  <code className="text-xs bg-muted px-1 rounded">{data.updated_by ?? 'n/a'}</code>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function FxRateCard() {
  const { data, isLoading } = useQuery({ queryKey: ['settings', 'fx'], queryFn: adminApi.getFxRate });

  const [value, setValue] = useState('');

  // Keep the editable field in sync with the server value when it changes
  // (initial load, after refresh, after save).
  useEffect(() => {
    if (data) setValue(String(data.value));
  }, [data]);

  const save = useMutation({
    mutationFn: () => adminApi.setFxRate(Number(value)),
    onSuccess: () => toast.success('已保存汇率'),
  });

  const refresh = useMutation({
    mutationFn: () => adminApi.refreshFxRate(),
    onSuccess: (r) => {
      // Pure preview: fill the value into the input, let the admin click
      // Save to actually persist. Never writes the DB implicitly.
      setValue(String(r.rate));
      toast.message(`从 ${r.source} 拉到 ${r.rate}`, { description: '点"保存"后才写入数据库' });
    },
  });

  const parsed = Number(value);
  const valid = Number.isFinite(parsed) && parsed > 0;
  const dirty = data ? String(data.value) !== value : false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">人民币 / 美元汇率 (CNY per USD)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          用于把 Moonshot 的 ¥ 官方价换算成 USD 填入 <code className="text-xs bg-muted px-1 rounded">model_pricing.cost_usd_*</code>。
          <br />
          <b>改汇率不影响已有扣费</b>（历史 USD 是固定快照）。OpenAI 类模型原生 USD 定价，不受此字段影响。
        </p>

        {isLoading && <p className="text-muted-foreground">{LOADING_LABELS.loading}</p>}
        {data && (
          <>
            <div className="grid gap-3 md:grid-cols-[1fr_auto_auto]">
              <Field label="当前汇率 (¥ / $1)">
                <Input
                  type="number"
                  step="0.0001"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                />
              </Field>
              <div className="flex items-end">
                <Button onClick={() => save.mutate()} disabled={!valid || !dirty || save.isPending}>
                  <Save className="h-4 w-4 mr-1" />
                  {save.isPending ? LOADING_LABELS.saving : '保存'}
                </Button>
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => refresh.mutate()}
                  disabled={refresh.isPending}
                >
                  <RefreshCw className={`h-4 w-4 mr-1 ${refresh.isPending ? 'animate-spin' : ''}`} />
                  {refresh.isPending ? LOADING_LABELS.fetching : '从网上拉最新'}
                </Button>
              </div>
            </div>

            <div className="text-xs text-muted-foreground space-y-0.5">
              <div>
                上次更新：{new Date(data.updated_at).toLocaleString('zh-CN')} · by <code className="text-xs bg-muted px-1 rounded">{data.updated_by ?? 'n/a'}</code>
              </div>
              {data.description && <div>{data.description}</div>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
